import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CryptoIcon from './CryptoIcon';
import { TrendingUp, TrendingDown } from 'lucide-react';
import axios from 'axios';

const API_URL = '';

/**
 * CryptoTicker Component
 * Scrolling ticker bar showing real-time crypto prices
 * Can be placed at the top or bottom of any page
 */
const CryptoTicker = ({ position = 'top', autoScroll = true }) => {
  const navigate = useNavigate();
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrices();
    
    // Refresh prices every 30 seconds
    const interval = setInterval(() => {
      fetchPrices();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchPrices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/market/prices`);
      if (response.data.success) {
        // Filter to show only major crypto pairs
        const cryptoPrices = response.data.prices.filter(p => p.type === 'crypto');
        setPrices(cryptoPrices);
      }
    } catch (error) {
      console.error('Error fetching ticker prices:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || prices.length === 0) {
    return null;
  }

  return (
    <div 
      className={`w-full overflow-hidden ${position === 'top' ? 'border-b' : 'border-t'}`}
      style={{
        background: 'linear-gradient(135deg, #0a1628 0%, #0f2847 100%)',
        borderColor: 'rgba(0, 200, 255, 0.2)'
      }}
    >
      <div className={`flex gap-6 py-2 px-4 ${autoScroll ? 'animate-scroll' : ''}`}>
        {/* Duplicate items for seamless scrolling */}
        {[...prices, ...prices].map((price, index) => {
          const cryptoSymbol = price.symbol.split('/')[0];
          const isPositive = price.change >= 0;

          return (
            <div 
              key={`${price.id}-${index}`}
              className="flex items-center gap-2 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/binary-trade', { state: { selectedPair: price.symbol } })}
            >
              <CryptoIcon symbol={cryptoSymbol} size={20} />
              
              <div className="flex items-center gap-2">
                <span className="text-white font-semibold text-sm">
                  {cryptoSymbol}
                </span>
                <span className="text-white font-bold text-sm">
                  ${price.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                
                <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${
                  isPositive ? 'bg-green-500/20' : 'bg-red-500/20'
                }`}>
                  {isPositive ? (
                    <TrendingUp className="w-3 h-3 text-green-400" />
                  ) : (
                    <TrendingDown className="w-3 h-3 text-red-400" />
                  )}
                  <span className={`text-xs font-semibold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                    {isPositive ? '+' : ''}{price.change.toFixed(2)}%
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <style jsx>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        .animate-scroll {
          animation: scroll 60s linear infinite;
        }
        
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default CryptoTicker;
